import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Award, ShieldCheck, GraduationCap } from "lucide-react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// Register GSAP plugins
if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger);
}

const AboutUs = () => {
  const navigate = useNavigate();
  const sectionRef = useRef(null);
  const imageRef = useRef(null);
  const titleRef = useRef(null);
  const textRef = useRef(null);
  const highlightsRef = useRef(null);
  const statsRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Animate image
      gsap.fromTo(
        imageRef.current,
        { opacity: 0, x: -100, scale: 0.9 },
        {
          opacity: 1,
          x: 0,
          scale: 1,
          duration: 1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: imageRef.current,
            start: "top 80%",
            end: "bottom 20%",
            toggleActions: "play none none reverse",
          },
        }
      );

      // Animate title
      gsap.fromTo(
        titleRef.current,
        { opacity: 0, y: 50 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          delay: 0.2,
          ease: "power2.out",
          scrollTrigger: {
            trigger: titleRef.current,
            start: "top 80%",
            end: "bottom 20%",
            toggleActions: "play none none reverse",
          },
        }
      );

      // Animate text
      gsap.fromTo(
        textRef.current,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          delay: 0.4,
          ease: "power2.out",
          scrollTrigger: {
            trigger: textRef.current,
            start: "top 80%",
            end: "bottom 20%",
            toggleActions: "play none none reverse",
          },
        }
      );

      // Animate highlights
      gsap.fromTo(
        highlightsRef.current,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          delay: 0.6,
          ease: "power2.out",
          scrollTrigger: {
            trigger: highlightsRef.current,
            start: "top 80%",
            end: "bottom 20%",
            toggleActions: "play none none reverse",
          },
        }
      );

      // Animate stats
      gsap.fromTo(
        statsRef.current,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          delay: 0.8,
          ease: "power2.out",
          scrollTrigger: {
            trigger: statsRef.current,
            start: "top 80%",
            end: "bottom 20%",
            toggleActions: "play none none reverse",
          },
        }
      );

      // Animate button
      gsap.fromTo(
        buttonRef.current,
        { opacity: 0, y: 20 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          delay: 1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: buttonRef.current,
            start: "top 80%",
            end: "bottom 20%",
            toggleActions: "play none none reverse",
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 px-6 md:px-16 bg-[#f6fdf9] flex flex-col md:flex-row items-center gap-12">

      {/* IMAGE SECTION */}
      <div className="flex-1">
        <img
          ref={imageRef}
          src="./home-about.avif"
          alt="About Greenmark"
          className="rounded-2xl shadow-lg hover:scale-105 transition-transform duration-700"
        />
      </div>

      {/* TEXT SECTION */}
      <div className="flex-1 text-gray-800">

        {/* TITLE */}
        <h3 ref={titleRef} className="text-3xl font-bold text-[#0b9949] mb-4">
          About Greenmark
        </h3>

        {/* MAIN ABOUT TEXT */}
        <div ref={textRef}>
          <p className="text-lg leading-relaxed mb-4">
            Greenmark is an ITE-approved training provider delivering PCM sector
            competency training for Electrical Fitting, Metal Scaffolding, Rigging & Signaling,
            and Welding works. Our programs ensure full compliance with R1 worker
            requirements under Singapore's Process, Construction & Maintenance (PCM) framework.
          </p>

          <p className="text-lg leading-relaxed mb-6">
            Every course is designed with real industry case studies, hands-on practice,
            and WSQ-certified trainers to ensure learners gain practical skills and
            successfully pass mandatory assessments.
          </p>

          {/* External Reference (Greensafe) */}
          <p className="text-sm text-gray-600 mb-6">
            For official reference, you may also visit the Greensafe International website: 
            <a 
              href="https://www.greensafe.com.sg" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#0b9949] font-semibold underline ml-1"
            >
              www.greensafe.com.sg
            </a>
          </p>
        </div>

        {/* ⭐ ACCREDITATION HIGHLIGHT */}
        <div ref={highlightsRef} className="grid grid-cols-2 gap-4 mb-8">
          <div className="flex items-center gap-2">
            <ShieldCheck className="text-[#0b9949]" />
            <span className="font-semibold">ITE Approved</span>
          </div>

          <div className="flex items-center gap-2">
            <Award className="text-[#0b9949]" />
            <span className="font-semibold">WSQ Certified Trainers</span>
          </div>

          <div className="flex items-center gap-2">
            <GraduationCap className="text-[#0b9949]" />
            <span className="font-semibold">PCM Worker Courses</span>
          </div>

          <div className="flex items-center gap-2">
            <CheckCircle className="text-[#0b9949]" />
            <span className="font-semibold">Assessment Ready</span>
          </div>
        </div>

        {/* CTA BUTTON */}
        <button
          ref={buttonRef}
          onClick={() => navigate("/about")}
          className="bg-[#0b9949] text-white px-8 py-3 rounded-full text-lg font-medium hover:bg-[#097c3c] transition-all"
        >
          Learn More About Us
        </button>

      </div>
    </section>
  );
};

export default AboutUs;
